import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Users, Download, FileText, CheckCircle, Clock } from 'lucide-react';
import { useDashboard } from './DashboardContext';

export const ContabileDashboard = () => {
  const {
    selectedMaestroFilter, setSelectedMaestroFilter,
    maestrosForFilter, fetchAthletesCreatedByMaestros,
    exportMaestroAthletesExcel, athletesCreatedByMaestros,
    downloadPaymentPdf, approveMaestroAthleteCreation
  } = useDashboard();

  return (
    <div className="space-y-6" data-testid="contabile-dashboard">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="w-5 h-5" />
            <span>Atleti Creati dai Maestri</span>
          </CardTitle>
          <CardDescription>Gestisci le richieste di approvazione atleti e verifica i pagamenti</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 pb-4 border-b">
            <div className="flex items-center space-x-2">
              <Label htmlFor="contabile-maestro-filter" className="text-sm whitespace-nowrap">Filtra per Maestro:</Label>
              <select
                id="contabile-maestro-filter"
                value={selectedMaestroFilter}
                onChange={(e) => {
                  setSelectedMaestroFilter(e.target.value);
                  fetchAthletesCreatedByMaestros(e.target.value === 'all' ? null : e.target.value);
                }}
                className="border rounded-md px-3 py-1.5 text-sm min-w-[180px]"
                data-testid="contabile-maestro-filter-select"
              >
                <option value="all">Tutti i Maestri</option>
                {maestrosForFilter.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={exportMaestroAthletesExcel}
              className="flex items-center space-x-2"
              data-testid="contabile-export-maestro-athletes-excel"
            >
              <Download className="w-4 h-4" />
              <span>Esporta Excel</span>
            </Button>
          </div>

          {athletesCreatedByMaestros.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              Nessun atleta creato dai maestri al momento.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-semibold">Data</th>
                      <th className="text-left p-3 font-semibold">Maestro</th>
                      <th className="text-left p-3 font-semibold">Atleta</th>
                      <th className="text-left p-3 font-semibold">Email</th>
                      <th className="text-left p-3 font-semibold">Azione</th>
                      <th className="text-left p-3 font-semibold">Status</th>
                      <th className="text-left p-3 font-semibold">PDF</th>
                      <th className="text-left p-3 font-semibold">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {athletesCreatedByMaestros.map((creation) => (
                      <tr key={creation.id} className="border-b hover:bg-gray-50">
                        <td className="p-3">
                          {new Date(creation.created_at).toLocaleDateString('it-IT', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </td>
                        <td className="p-3">
                          <span className="font-medium text-orange-600">{creation.maestro_name}</span>
                        </td>
                        <td className="p-3">
                          <span className="font-medium">{creation.athlete_name}</span>
                        </td>
                        <td className="p-3 text-gray-500">{creation.athlete_email}</td>
                        <td className="p-3">
                          {creation.action_type === 'creation' ? (
                            <Badge className="bg-green-100 text-green-800">Nuova Creazione</Badge>
                          ) : (
                            <Badge className="bg-orange-100 text-orange-800">Password Sovrascritta</Badge>
                          )}
                        </td>
                        <td className="p-3">
                          {creation.status === 'pending' ? (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              <Clock className="w-3 h-3 mr-1" />
                              In Attesa
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Approvato
                            </Badge>
                          )}
                        </td>
                        <td className="p-3">
                          {creation.has_payment_pdf ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => downloadPaymentPdf(creation.id, creation.payment_pdf_filename)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              data-testid={`contabile-download-pdf-${creation.id}`}
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              Scarica
                            </Button>
                          ) : (
                            <span className="text-gray-400 text-xs">N/A</span>
                          )}
                        </td>
                        <td className="p-3">
                          {creation.status === 'pending' && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => approveMaestroAthleteCreation(creation.id)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`contabile-approve-athlete-${creation.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approva
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
